import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import React, { useCallback, useMemo, useState } from "react";
import type { ComponentType } from "react";
import * as Sentry from "@sentry/react";
import ButtonsSection from "components/ui/buttons/ButtonsSection";
import { Drawer } from "vaul";
import "../../../src/app/Vaul.css";
import type { ExistingRecord } from "../check-in/checkin-context";
import { CheckInProvider } from "../check-in/checkin-context";
import { CheckInAdd } from "../check-in/CheckInAdd/CheckInAdd";
import CheckInView from "../check-in/CheckInView/CheckInView";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TypingHeading from "../home/components/TypingHeading";

const CENTERED_CONTAINER_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "calc(100vh - 80px)",
} as const;

const INNER_WRAPPER_STYLE = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
} as const;

type CheckInState = {
  feelingName?: string | null;
  selectedDate?: string | Date | null;
  existingRecord?: ExistingRecord | null;
  feelingNames?: string[];
  imageSrc?: string;
};

type CheckInAddProps = {
  stateOverride?: CheckInState | null;
  onClose?: () => void;
  onSaveSuccess?: (result: unknown) => void;
};

type CheckInViewProps = {
  stateOverride?: CheckInState | null;
  onClose?: () => void;
};

const CheckInAddWithState = CheckInAdd as ComponentType<CheckInAddProps>;
const CheckInViewWithState = CheckInView as ComponentType<CheckInViewProps>;

const getDateFromSelectedDate = (value: string | Date | null | undefined) => {
  if (value instanceof Date) {
    return value;
  }
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isWeekendDay = (value: Date | null) => {
  if (!value) {
    return false;
  }
  const day = value.getDay();
  return day === 6 || day === 0;
};

/**
 * Home component - uses modern React pattern with custom hooks
 *
 * This component:
 * - Uses custom hook (useHomeData) for all data fetching and side effects
 * - Focuses on presentation/rendering
 * - Still testable by mocking the useHomeData hook
 * - Follows modern React conventions (2024)
 */
export default function Intro3() {
  const { t } = useTranslation();
  const [currentScreen, setCurrentScreen] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();

  const handleContinue = () => {
    // Go directly to Analyze team moods screen
    setIsTransitioning(true);
    setTimeout(() => {
      //setCurrentScreen(2);
      setIsTransitioning(false);
      //localStorage.setItem("isIntroSeen", true);
      //navigate("/app/home");
      navigate("/app/intro5");
    }, 300);
  };

  const [isExiting, setIsExiting] = useState(false);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        zIndex: 9999,
        backgroundColor: "#fafafa",
        //background: 'linear-gradient(to bottom right, #e0f2fe, #ffffff, #f3e8ff)',
        //transition: 'opacity 0.5s ease',
        opacity: isExiting ? 0 : 1,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Main Content */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          height: "100%",
          width: "100%",
          maxWidth: "100%",
          padding: "24px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* Screen 1: Welcome to Experience */}
        {
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
                maxWidth: "100%",
                flex: 1,
                paddingTop: "40px",
              }}
            >
              {/* App Icon/Logo */}
              <div>
                <img
                  src="/logo512.png"
                  alt="Experience App Icon"
                  style={{
                    objectFit: "contain",
                    padding: "8px",
                    width: "156px",
                    height: "156px",
                  }}
                  onError={(e) => {
                    // Fallback to logo192 if logo512 doesn't exist
                    /*
                                        if (e.target.src !== "/logo192.png") {
                                            e.target.src = "/logo192.png";
                                        }

                                         */
                  }}
                />
              </div>

              {/* Title */}
              <h1
                style={{
                  fontSize: "clamp(32px, 8vw, 36px)",
                  fontWeight: "bold",
                  color: "#111827",
                  marginTop: "48px",
                  textAlign: "center",
                  transition: "all 0.5s ease",
                  opacity: isExiting ? 0 : 1,
                  transform: isExiting ? "translateY(16px)" : "translateY(0)",
                  width: "100%",
                  maxWidth: "100%",
                  padding: "0 16px",
                  boxSizing: "border-box",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                <TypingHeading
                  fullText={"Reflxect for yourself - means to care of others"}
                  /*
                  typingOptions={{
                    skipOnInternalNavigation: false,
                    typingSpeed: 80,
                  }}*/
                />
              </h1>
              <h2
                style={{
                  fontSize: "clamp(16px, 2vw, 20px)",
                  color: "#666",
                  marginBottom: "48px",
                  textAlign: "center",
                  transition: "all 0.5s ease",
                  opacity: isExiting ? 0 : 1,
                  fontWeight: "450",
                  marginTop: "0",
                }}
              >
                Productivity through self-awareness new
              </h2>
            </div>

            {/* Continue Button */}
            <button onClick={handleContinue} className="proceed-button">
              Continue
            </button>
          </>
        }
      </div>

      {/* Animated Background Elements */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "80px",
            left: "40px",
            width: "80px",
            height: "80px",
            backgroundColor: "#bfdbfe",
            borderRadius: "50%",
            opacity: 0.2,
            filter: "blur(40px)",
            animation: "float 6s ease-in-out infinite",
            animationDelay: "0s",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "80px",
            right: "40px",
            width: "128px",
            height: "128px",
            backgroundColor: "#e9d5ff",
            borderRadius: "50%",
            opacity: 0.2,
            filter: "blur(40px)",
            animation: "float 8s ease-in-out infinite",
            animationDelay: "2s",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "25%",
            width: "64px",
            height: "64px",
            backgroundColor: "#fbcfe8",
            borderRadius: "50%",
            opacity: 0.2,
            filter: "blur(40px)",
            animation: "float 7s ease-in-out infinite",
            animationDelay: "1s",
          }}
        />
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateX(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
      `}</style>
    </div>
  );
}
